import { CornerDownLeft } from 'lucide-react';
import { memo } from 'react';

import { cn } from '../../lib/cn';

export interface FollowUpHintsProps {
  questions: string[];
  /**
   * Sends the tapped question as a new user message. Absent in read-only
   * contexts (admin inbox) — suggestions then render as plain, non-interactive
   * text.
   */
  onSelect?: (question: string) => void;
}

const baseItemClassName = cn(
  'flex max-w-full items-center gap-1.5 text-left text-xs leading-snug text-chat-muted-foreground',
);

const interactiveItemClassName = cn(
  baseItemClassName,
  'cursor-pointer transition-colors hover:text-chat-foreground hover:underline',
);

/**
 * Follow-up suggestions rendered under the last assistant message. Tapping one
 * sends it as a normal user message (via `onSelect`); without a handler they
 * are read-only text.
 */
export const FollowUpHints = memo(
  ({ questions, onSelect }: FollowUpHintsProps) => {
    if (questions.length === 0) {
      return null;
    }

    return (
      <div className='mt-2.5 flex flex-col items-start gap-1 border-t border-chat-border/40 pt-2.5'>
        {questions.map(question =>
          onSelect ? (
            <button
              key={question}
              type='button'
              className={interactiveItemClassName}
              onClick={() => onSelect(question)}
            >
              <CornerDownLeft className='size-3 shrink-0 opacity-60' />
              <span>{question}</span>
            </button>
          ) : (
            <span key={question} className={baseItemClassName}>
              <CornerDownLeft className='size-3 shrink-0 opacity-60' />
              <span>{question}</span>
            </span>
          ),
        )}
      </div>
    );
  },
);
